import {useEffect} from 'react';
import {useSelector} from 'react-redux';

import deviceSettingTypes from '../../../consts/device/deviceSettingTypes';
import connectivityService from '../../../services/device/connectivityService';
import appRouterService from '../../../services/route/appRouterService';
import {makeSelectIotDeviceMergedWithConsumerProduct} from '../../../state/selectors/consumer';
import {selectIsDeviceReady} from '../../../state/selectors/iotDevice';
import helpers from '../../../utils/helpers';
import useAemFlexBatteryEnabled from './enabledFeatures/useAemFlexBatteryEnabled';
import useAemPauseModeEnabled from './enabledFeatures/useAemPauseModeEnabled';
import useAutoStartEnabled from './enabledFeatures/useAutoStartEnabled';
import useCleaningModeEnabled from './enabledFeatures/useCleaningModeEnabled';
import useDeviceDiagnosticModeEnabled from './enabledFeatures/useDeviceDiagnosticModeEnabled';
import useFlexPuffEnabled from './enabledFeatures/useFlexPuffEnabled';
import useGesturesEnabled from './enabledFeatures/useGesturesEnabled';
import useIlluminationModeEnabled from './enabledFeatures/useIlluminationModeEnabled';
import useLocateMyDeviceEnabled from './enabledFeatures/useLocateMyDeviceEnabled';
import usePreferenceCenterEnabled from './enabledFeatures/usePreferenceCenterEnabled';
import useRenameDeviceEnabled from './enabledFeatures/useRenameDeviceEnabled';
import useResponsiveDrawEnabled from './enabledFeatures/useResponsiveDrawEnabled';
import useRestartMyDeviceModeEnabled from './enabledFeatures/useRestartMyDeviceModeEnabled';
import useUpdateAvailableEnabled from './enabledFeatures/useUpdateAvailableEnabled';
import useUsageEnabled from './enabledFeatures/useUsageEnabled';
import useVapeCloudModeEnabled from './enabledFeatures/useVapeCloudModeEnabled';
import useVapeSessionEnabled from './enabledFeatures/useVapeSessionModeEnabled';
import useVibrationModeEnabled from './enabledFeatures/useVibrationModeEnabled';
import useDisableFeature from './useDisableFeature';

const useClearSettingOnDisable = (settingType, isClearDisabled) => {
    const {disabled, onDisableClick} = useDisableFeature(settingType);

    const isDeviceReady = useSelector(selectIsDeviceReady);
    const isUSB = connectivityService.isUsb();
    const isShowMsg = !isUSB || isDeviceReady;
    const iotDevice = useSelector(makeSelectIotDeviceMergedWithConsumerProduct());
    const isAemFlexBatteryEnabled = useAemFlexBatteryEnabled(iotDevice);
    const isAemPauseModeEnabled = useAemPauseModeEnabled(iotDevice);
    const isAutoStartEnabled = useAutoStartEnabled(iotDevice);
    const isFlexPuffEnabled = useFlexPuffEnabled(iotDevice);
    const isCleaningModeEnabled = useCleaningModeEnabled(iotDevice);
    const {isConnectCareModeEnabled, isAdvancedDeviceDiagnosticModeEnabled} = useDeviceDiagnosticModeEnabled(iotDevice);
    const isDeviceRenameEnabled = useRenameDeviceEnabled(iotDevice);
    const isGesturesEnabled = useGesturesEnabled(iotDevice);
    const {isIlluminationModeEnabled, isExtendedIlluminationModeEnabled} = useIlluminationModeEnabled(iotDevice);
    const isLocateMyDeviceEnabled = useLocateMyDeviceEnabled(iotDevice);
    const isPreferenceCenterEnabled = usePreferenceCenterEnabled();
    const isResponsiveDrawModeEnabled = useResponsiveDrawEnabled(iotDevice);
    const isRestartMyDeviceModeEnabled = useRestartMyDeviceModeEnabled(iotDevice);
    const isVapeCloudModeEnabled = useVapeCloudModeEnabled(iotDevice);
    const isVapeSessionEnabled = useVapeSessionEnabled(iotDevice);
    const isVibrationModeEnabled = useVibrationModeEnabled(iotDevice);
    const isUpdateAvailableEnabled = useUpdateAvailableEnabled();
    const isUsageStatsEnabled = useUsageEnabled();
    const updateFlags = {
        [deviceSettingTypes.AEM_FLEX_BATTERY]: isAemFlexBatteryEnabled,
        [deviceSettingTypes.AEM_PAUSE_MODE]: isAemPauseModeEnabled,
        [deviceSettingTypes.AUTO_START_MODE]: isAutoStartEnabled,
        [deviceSettingTypes.FLEX_PUFF]: isFlexPuffEnabled,
        [deviceSettingTypes.CLEANING_MODE]: isCleaningModeEnabled,
        [deviceSettingTypes.CONNECT_CARE]: isConnectCareModeEnabled,
        [deviceSettingTypes.DEVICE_ADVANCED_DIAGNOSTIC]: isAdvancedDeviceDiagnosticModeEnabled,
        [deviceSettingTypes.DEVICE_RENAME]: isDeviceRenameEnabled,
        [deviceSettingTypes.FWU_UPDATE_AVAILABLE]: isUpdateAvailableEnabled,
        [deviceSettingTypes.GET_FEATURE_SECTION]: isUpdateAvailableEnabled,
        [deviceSettingTypes.GESTURE_MODE]: isGesturesEnabled,
        [deviceSettingTypes.LED_SETTING]: isIlluminationModeEnabled,
        [deviceSettingTypes.EXTENDED_LED_SETTING]: isExtendedIlluminationModeEnabled,
        [deviceSettingTypes.LOCATE_MY_DEVICE]: isLocateMyDeviceEnabled,
        [deviceSettingTypes.RESPONSIVE_DRAW]: isResponsiveDrawModeEnabled,
        [deviceSettingTypes.RESTART]: isRestartMyDeviceModeEnabled,
        [deviceSettingTypes.TNC_PREFERENCE_CENTER]: isPreferenceCenterEnabled,
        [deviceSettingTypes.VAPE_CLOUD_SIZE]: isVapeCloudModeEnabled,
        [deviceSettingTypes.VAPE_SESSION]: isVapeSessionEnabled,
        [deviceSettingTypes.VIBRATION_MODE]: isVibrationModeEnabled,
        [deviceSettingTypes.USAGE_MODE]: isUsageStatsEnabled,
        [deviceSettingTypes.ABOUT_MY_DEVICE]: true,
        [deviceSettingTypes.FWU_UPDATE_CHECK]: true,
    };
    const isEnabled = updateFlags[settingType];

    useEffect(() => {
        if (isClearDisabled) return;

        if (disabled || !isEnabled) {
            if (isShowMsg) {
                helpers.runFunction(onDisableClick);
            }
            appRouterService.forwardToDeviceSettingsPage();
        }
    }, [isEnabled, disabled, isClearDisabled]);
};

export default useClearSettingOnDisable;
